import React, { useState, useRef, useCallback } from 'react';
import './awss3.css';

const AWSS3Uploader = ({ onUploadComplete, folder = "general" }) => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [status, setStatus] = useState(null); // null | 'uploading' | 'success' | 'error'
    const [errorMsg, setErrorMsg] = useState('');
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef(null);
    const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:4000";

    const processFile = (selectedFile) => {
        if (!selectedFile) return;
        setFile(selectedFile);
        setStatus(null);
        setErrorMsg('');
        // Generate local preview
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(selectedFile);
    };

    const handleFileChange = (e) => {
        processFile(e.target.files[0]);
    };

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setDragging(false);
        const dropped = e.dataTransfer.files[0];
        if (dropped) processFile(dropped);
    }, []);

    const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
    const handleDragLeave = () => setDragging(false);

    const clearFile = (e) => {
        e.stopPropagation();
        setFile(null);
        setPreview(null);
        setStatus(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    const uploadToBackend = () => {
        return new Promise((resolve, reject) => {
            const formData = new FormData();
            formData.append("file", file);
            const token = localStorage.getItem("token");
            const xhr = new XMLHttpRequest();
            xhr.open("POST", `${BASE_URL}/upload/image?folder=${folder}`);
            if (token) xhr.setRequestHeader("Authorization", token);
            xhr.onload = () => {
                try {
                    const parsed = JSON.parse(xhr.responseText);
                    if (xhr.status === 200 && parsed.success && parsed.data?.url) {
                        resolve(parsed.data.url);
                    } else {
                        reject(new Error(parsed.message || `Upload failed (${xhr.status})`));
                    }
                } catch (e) {
                    reject(new Error("Invalid server response"));
                }
            };
            xhr.onerror = () => reject(new Error("Network error — check server connection"));
            xhr.send(formData);
        });
    };

    const handleUpload = async () => {
        if (!file || status === 'uploading') return;
        try {
            setStatus('uploading');
            setErrorMsg('');
            const url = await uploadToBackend();
            onUploadComplete(url);
            setStatus('success');
        } catch (err) {
            console.error("Upload error:", err);
            setStatus('error');
            setErrorMsg(err.message || 'Upload failed');
        }
    };

    const isUploading = status === 'uploading';
    const isSuccess = status === 'success';
    const isError = status === 'error';

    const dropZoneClass = [
        'dropZone',
        dragging ? 'dragging' : '',
        file && !isUploading ? 'hasFile' : '',
        isUploading ? 'uploading' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className="uploaderWrap">
            {/* Drop Zone */}
            <div
                className={dropZoneClass}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => !isUploading && inputRef.current?.click()}
                style={{ cursor: isUploading ? 'not-allowed' : 'pointer' }}
            >
                <input
                    ref={inputRef}
                    type="file"
                    onChange={handleFileChange}
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    style={{ display: 'none' }}
                    disabled={isUploading}
                />

                {/* Icon */}
                <div className="dropIcon">
                    {isUploading ? (
                        <span className="mdi mdi-cloud-upload-outline" style={{ animation: 'none' }} />
                    ) : isSuccess ? (
                        <span className="mdi mdi-check-circle-outline" />
                    ) : (
                        <span className="mdi mdi-image-plus" />
                    )}
                </div>

                {/* Text */}
                <div className="dropText">
                    {isUploading ? (
                        <strong>Uploading to Cloudinary...</strong>
                    ) : isSuccess ? (
                        <strong>Image uploaded!</strong>
                    ) : file ? (
                        <strong>Ready to upload</strong>
                    ) : (
                        <>
                            <strong>Drop image here or click to browse</strong>
                            <span>JPG, PNG, WEBP, GIF — max 5MB</span>
                        </>
                    )}
                </div>

                {/* File chip */}
                {file && !isUploading && (
                    <div className="fileChip">
                        <span className="mdi mdi-file-image-outline" style={{ color: '#f4632a' }} />
                        <span title={file.name}>{file.name}</span>
                        {!isSuccess && (
                            <button className="fileChipRemove" onClick={clearFile} title="Remove">
                                ×
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* Progress bar while uploading */}
            {isUploading && (
                <div className="progressBar">
                    <div className="progressFill" />
                </div>
            )}

            {/* Status badge */}
            {isSuccess && (
                <div className="statusBadge success">
                    <span className="mdi mdi-check-circle" />
                    Uploaded successfully
                </div>
            )}
            {isError && (
                <div className="statusBadge error">
                    <span className="mdi mdi-alert-circle-outline" />
                    {errorMsg}
                </div>
            )}

            {/* Upload button */}
            {file && !isSuccess && (
                <button
                    className="uploadBtn"
                    onClick={handleUpload}
                    disabled={isUploading}
                >
                    {isUploading ? (
                        <>
                            <span className="mdi mdi-loading mdi-spin" />
                            Uploading...
                        </>
                    ) : (
                        <>
                            <span className="mdi mdi-cloud-upload" />
                            Upload Image
                        </>
                    )}
                </button>
            )}
        </div>
    );
};

export default AWSS3Uploader;